/**
 * A sandbox call as the agent socket carries it (backend `web/protocol.py` SANDBOX, frozen there):
 * what the server asks the browser to run for a tool call, and what the browser hands back. Snake
 * case, as on the wire; each op maps onto one `Executor` method (types.ts). The fixture test keeps
 * the op names and error codes equal to the backend's.
 */
import { encoder } from "./bytes";
import type { ReplayEntry, ReplLanguage, SandboxErrorCode } from "./types";

export interface SandboxOpArgs {
  bash: { command: string };
  /** `cell_id` and `journal` only to replay a parked cell (C14, PK3). */
  repl: { language: ReplLanguage; code: string; cell_id?: string; journal?: ReplayEntry[] };
  /** `path` is absolute, under /workspace or /context. */
  read: { path: string; offset?: number; limit?: number; pages?: string };
  write: { path: string; content: string };
  edit: { path: string; old: string; new: string };
  /** V4A. */
  apply_patch: { patch: string };
  /** Sync the file now (its delta applied), so the server can read it (a native read, look). */
  stage: { path: string };
  /** Mount an export the server made at `path` (under /workspace/data), fetched lazily from `url`. */
  load: { path: string; url: string; size: number; content_type: string };
}

export type SandboxOp = keyof SandboxOpArgs;

export interface WireExecResult {
  stdout: string;
  stderr: string;
  exit_code: number;
  truncated: boolean;
  duration_ms: number;
  timed_out?: boolean;
}

export interface SandboxOpResults {
  bash: WireExecResult;
  repl: WireExecResult & {
    value?: string | null;
    reset_state?: boolean;
    /** The cell parked on these choices (their wire keys), with its clock reads and random bytes,
     * every one in seq order, and `journal_length` their count (the server keeps its tool calls,
     * and refuses any other count as INCOMPLETE); C14, PK3. */
    parked?: { choices: string[]; journal_length: number; journal: ReplayEntry[] };
  };
  read: {
    path: string;
    kind: "text" | "pdf" | "image" | "binary";
    text: string;
    truncated: boolean;
    total_lines?: number | null;
    total_pages?: number | null;
  };
  write: { path: string; bytes: number };
  edit: { path: string; bytes: number };
  apply_patch: {
    changes: { path: string; op: "add" | "update" | "delete" | "move"; moved_to?: string | null }[];
  };
  stage: { path: string; sha256: string; size: number };
  load: { path: string };
}

/** What a paused run lists as `run.waiting[].client` for a call the browser runs. */
export type SandboxClientCall = {
  [K in SandboxOp]: {
    kind: "sandbox";
    /** Answer with `sandbox.result {call: id}`. */
    id: string;
    /** The workspace it runs in: only the tab holding its lease runs it. */
    workspace: string;
    op: K;
    args: SandboxOpArgs[K];
    /** ISO time; past it the model is told the call timed out. */
    deadline: string;
    /** 2 when the call was routed again to another host (ER5). */
    attempt: 1 | 2;
    /** The host election's epoch it was routed under (0, NO_ELECTION, until ER3 routes by the election). */
    epoch: number;
  };
}[SandboxOp];

/** Each key of `T` once: the object literal must name them all and no other. */
const keysOf = <T>(keys: Record<keyof T, true>) => Object.keys(keys) as (keyof T & string)[];

/** The fields of `SandboxClientCall` and of each result, for the fixture test against the
 * backend's (`SANDBOX.client`, `SANDBOX.results`, named as there). */
export const SANDBOX_CLIENT_FIELDS = keysOf<SandboxClientCall>({
  kind: true,
  id: true,
  workspace: true,
  op: true,
  args: true,
  deadline: true,
  attempt: true,
  epoch: true,
});

export const SANDBOX_RESULT_FIELDS = {
  exec: keysOf<SandboxOpResults["bash"]>({
    stdout: true,
    stderr: true,
    exit_code: true,
    truncated: true,
    duration_ms: true,
    timed_out: true,
  }),
  repl: keysOf<SandboxOpResults["repl"]>({
    stdout: true,
    stderr: true,
    exit_code: true,
    truncated: true,
    duration_ms: true,
    timed_out: true,
    value: true,
    reset_state: true,
    parked: true,
  }),
  read: keysOf<SandboxOpResults["read"]>({
    path: true,
    kind: true,
    text: true,
    truncated: true,
    total_lines: true,
    total_pages: true,
  }),
  write: keysOf<SandboxOpResults["write"]>({ path: true, bytes: true }),
  patch: keysOf<SandboxOpResults["apply_patch"]>({ changes: true }),
  stage: keysOf<SandboxOpResults["stage"]>({ path: true, sha256: true, size: true }),
  load: keysOf<SandboxOpResults["load"]>({ path: true }),
};

/** The `sandbox.result` command's answer: exactly one of `result` and `error`. */
export type SandboxHandIn =
  | { call: string; result: SandboxOpResults[SandboxOp]; error?: null }
  | { call: string; result?: null; error: { code: SandboxErrorCode; message: string } };

export const SANDBOX_OPS = [
  "bash",
  "repl",
  "read",
  "write",
  "edit",
  "apply_patch",
  "stage",
  "load",
] as const satisfies readonly SandboxOp[];

/**
 * A sandbox host's report on this tab (C4), sent with `hello` and `presence.update` as `executor`
 * (debounced 5 s; null when the tab cannot host). Host election (ER1-ER2) reads it.
 */
export interface Candidate {
  candidate: boolean;
  /** 0-100. */
  score: number;
  toolchain_cached: boolean;
  reasons: CandidateReason[];
}
export type CandidateReason =
  | "phone"
  | "cellular"
  | "save_data"
  | "low_battery"
  | "low_memory"
  | "hidden"
  | "no_wasm"
  | "boot_failed";

/**
 * Code mode's bindings (C11, backend `agentic/codemode/layout.py`): every generated stub calls
 * `__steddy_call(name, argsJson)` (on globalThis in JS, builtins in Python), which resolves to a
 * JSON `ToolCallEnvelope`; `tools.wait` is the same function under `WAIT_CALL`.
 */
export const HOST_FUNCTION = "__steddy_call";
export const WAIT_CALL = "sandbox.wait";
/** Absolute; the backend's `layout.TOOLS_DIR` ("tools") is the same directory relative to /context. */
export const TOOLS_PATH = "/context/tools";

export type ToolCallEnvelope =
  | { ok: true; value: unknown }
  | {
      ok: false;
      /** retryable: CM3 may answer the same call again later (its `running` refusal; C14, PK2). */
      error: { code: string; message: string; outcome: string; retryable?: boolean };
    };
/** The value of a write that needs the user's approval: its card is shown as usual. */
export interface ApprovalRequired {
  status: "approval_required";
  choice: string;
}
/** `tools.wait`'s arguments and value, as `WAIT_CALL` carries them. */
export interface WaitArgs {
  choices: string[];
  /** null: no timeout of its own; the wait lasts until the cell's own clock ends. */
  timeout_ms: number | null;
}
/** Each waited choice's outcome; pending when the timeout ran out first. */
export type WaitValue = Record<string, "approved" | "denied" | "pending">;

/** A refused or failed call's envelope, as JSON (what `__steddy_call` resolves to). */
export function refusal(code: string, message: string, outcome = "denied"): string {
  return JSON.stringify({
    ok: false,
    error: { code, message, outcome },
  } satisfies ToolCallEnvelope);
}

/** The refusal of a call made while no cell runs (between cells, or from work a finished one left). */
export function notInCell(): string {
  return refusal("not_available", "Tools can be called only from a repl cell, while it runs.");
}

/**
 * What one cell call is to the page: the host's ticket for the repl call the cell runs in (null
 * when the runtime was called without one), its seq (from 1 in each cell), the tool and its
 * arguments. The page maps the ticket to that call's client id, CM3's `call`.
 */
export interface HostCall {
  ticket: number | null;
  seq: number;
  name: string;
  argsJson: string;
}

/** CM3's limit on a cell's calls (backend `code_calls.SEQ_MAX`). */
export const SEQ_MAX = 10_000;
/** A tool's name as CM3's socket takes it (backend `web/protocol.py` TOOL_NAME). */
export const TOOL_NAME_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
/** A wait's choice ids, 1 to 20 of 1 to 200 characters each (backend `web/protocol.py` CHOICE_IDS). */
export const WAIT_CHOICES_MAX = 20;
export const CHOICE_ID_MAX_CHARS = 200;
/**
 * The most a call's arguments may be, as UTF-8 JSON. Mirrors AGENT_WAIT_PAYLOAD_MAX_BYTES (backend
 * settings, 128 KB): the frame carrying them must stay far below the socket's own limit, past which
 * the server closes the user's agent socket. Also the most of an answer a cell's journal keeps.
 */
export const CALL_ARGS_MAX_BYTES = 128 * 1024;

/** CM3's answer to one call (`sandbox.tool_result` without call and seq; backend `code_calls._send`). */
export interface ToolResultAnswer {
  status: "ok" | "approval_required" | "denied" | "error";
  result?: unknown;
  choice?: string;
  error?: { code: string; message: string; outcome: string; retryable?: boolean };
}

/** UTF-8 bytes of `text`, counted without encoding it when it is plainly over `max`. */
export function utf8Bytes(text: string, max = Infinity): number {
  // A UTF-16 unit is at least one byte.
  if (text.length > max) return text.length;
  return encoder.encode(text).length;
}

/**
 * Why CM3's socket would refuse this call as a command error (no sandbox.tool_result comes back),
 * so the runtime refuses it before it takes a seq; null when it would take it.
 */
export function callProblem(name: string, argsJson: string): string | null {
  if (utf8Bytes(argsJson, CALL_ARGS_MAX_BYTES) > CALL_ARGS_MAX_BYTES) {
    return `A tool call's arguments are at most ${CALL_ARGS_MAX_BYTES / 1024} KB of JSON.`;
  }
  if (name !== WAIT_CALL && !TOOL_NAME_PATTERN.test(name)) {
    return "A tool's name is lowercase letters, digits and underscores, starting with a letter.";
  }
  let args: unknown;
  try {
    args = JSON.parse(argsJson);
  } catch {
    args = undefined;
  }
  if (typeof args !== "object" || args === null || Array.isArray(args)) {
    return name === WAIT_CALL
      ? "A wait's arguments are {choices: [choice ids], timeout_ms: number | null}."
      : "A tool's arguments are a JSON object.";
  }
  if (name !== WAIT_CALL) return null;
  const { choices, timeout_ms } = args as Partial<WaitArgs>;
  // Python's len, as the server counts: code points.
  const fits = (choice: unknown) =>
    typeof choice === "string" && choice.length > 0 && [...choice].length <= CHOICE_ID_MAX_CHARS;
  if (
    !Array.isArray(choices) ||
    choices.length < 1 ||
    choices.length > WAIT_CHOICES_MAX ||
    !choices.every(fits)
  ) {
    return `A wait takes 1 to ${WAIT_CHOICES_MAX} choice ids of 1 to ${CHOICE_ID_MAX_CHARS} characters.`;
  }
  if (
    timeout_ms !== null &&
    timeout_ms !== undefined &&
    !(Number.isSafeInteger(timeout_ms) && timeout_ms >= 0)
  ) {
    return "A wait's timeout_ms is a whole number of 0 or more, or null.";
  }
  return null;
}

/**
 * The command a host call is to the server (CM3): `sandbox.wait` for `WAIT_CALL`, else
 * `sandbox.tool_call`. `call` is the client id of the repl call the cell runs in. Null for a call
 * the socket would refuse (callProblem), which the runtime answers invalid without sending.
 */
export function toolCallCommand(
  call: string,
  { seq, name, argsJson }: Pick<HostCall, "seq" | "name" | "argsJson">
):
  | {
      type: "sandbox.tool_call";
      args: { call: string; seq: number; name: string; args: Record<string, unknown> };
    }
  | {
      type: "sandbox.wait";
      args: { call: string; seq: number; choices: string[]; timeout_ms: number | null };
    }
  | null {
  if (callProblem(name, argsJson)) return null;
  const args = JSON.parse(argsJson) as Record<string, unknown>;
  if (name !== WAIT_CALL) return { type: "sandbox.tool_call", args: { call, seq, name, args } };
  const { choices, timeout_ms } = args as Partial<WaitArgs>;
  return {
    type: "sandbox.wait",
    args: { call, seq, choices: choices!, timeout_ms: timeout_ms ?? null },
  };
}

/** CM3's answer as the C11 envelope the cell gets. */
export function toolResultEnvelope(answer: ToolResultAnswer): ToolCallEnvelope {
  switch (answer.status) {
    case "ok":
      return { ok: true, value: answer.result ?? null };
    case "approval_required":
      return {
        ok: true,
        value: {
          status: "approval_required",
          choice: String(answer.choice ?? ""),
        } satisfies ApprovalRequired,
      };
    default:
      return {
        ok: false,
        error: {
          code: String(answer.error?.code ?? answer.status),
          message: String(answer.error?.message ?? "The tool call failed."),
          outcome: String(
            answer.error?.outcome ?? (answer.status === "denied" ? "denied" : "error")
          ),
          ...(answer.error?.retryable === true && { retryable: true }),
        },
      };
  }
}
